import React, { useState, useEffect } from 'react';
import { BarChart3, TrendingUp, AlertTriangle, MapPin } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { FLOORS, FLOOR_LAYOUTS } from '../mockData';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useTranslation } from 'react-i18next';

const Analytics = () => {
  const { t } = useTranslation();
  const [assets, setAssets] = useState([]);
  const [selectedFloor, setSelectedFloor] = useState(0);

  useEffect(() => {
    const storedAssets = localStorage.getItem('e2-assets');
    if (storedAssets) {
      setAssets(JSON.parse(storedAssets));
    }
  }, []);

  // Calculate most used assets
  const mostUsedAssets = assets
    .map(asset => ({
      ...asset,
      usageScore: asset.status === 'in-use' ? 10 : asset.status === 'to-clean' ? 5 : 1
    }))
    .sort((a, b) => b.usageScore - a.usageScore)
    .slice(0, 5);

  // Calculate "lost" assets (those that moved a lot or have low battery)
  const lostAssets = assets
    .map(asset => ({
      ...asset,
      lostScore: (asset.path?.length || 0) + (asset.batteryLevel < 50 ? 5 : 0)
    }))
    .sort((a, b) => b.lostScore - a.lostScore)
    .slice(0, 5);

  // Heatmap data per floor
  const getHeatmapData = (floorId) => {
    const floorAssets = assets.filter(a => a.floor === floorId);
    const layout = FLOOR_LAYOUTS[floorId];
    
    return layout.rooms.map(room => {
      const roomAssets = floorAssets.filter(a => a.room === room.id);
      const inUseCount = roomAssets.filter(a => a.status === 'in-use').length;
      return {
        ...room,
        assetCount: roomAssets.length,
        inUseCount,
        heatLevel: inUseCount
      };
    });
  };

  const heatmapData = getHeatmapData(selectedFloor);
  const maxHeat = Math.max(...heatmapData.map(r => r.heatLevel), 1);

  const getHeatColor = (level) => {
    const intensity = level / maxHeat;
    if (intensity === 0) return '#E5E7EB';
    if (intensity < 0.3) return '#BFDBFE';
    if (intensity < 0.6) return '#60A5FA';
    if (intensity < 0.8) return '#3B82F6';
    return '#2563EB';
  };

  // Recommendations
  const recommendations = [];
  
  // Check for unused assets
  const unusedAssets = assets.filter(a => a.status === 'ready').length;
  if (unusedAssets > 5) {
    recommendations.push({
      type: 'optimization',
      title: `${unusedAssets} assets are currently unused`,
      description: `Consider redistributing resources or reducing inventory costs.`
    });
  }

  // Check for overcrowded floors
  FLOORS.forEach(floor => {
    const floorAssets = assets.filter(a => a.floor === floor.id);
    const inUseCount = floorAssets.filter(a => a.status === 'in-use').length;
    const utilization = floorAssets.length > 0 ? (inUseCount / floorAssets.length) * 100 : 0;
    
    if (utilization > 80) {
      recommendations.push({
        type: 'alert',
        title: `${floor.name} - ${floor.department} is at ${Math.round(utilization)}% capacity`,
        description: `Consider moving assets from less busy floors to meet demand.`
      });
    } else if (utilization < 30 && floorAssets.length > 3) {
      recommendations.push({
        type: 'info',
        title: `Low utilization on ${floor.name} - ${floor.department}`,
        description: `${floorAssets.length - inUseCount} assets could be redistributed to busier areas.`
      });
    }
  });

  return (
    <div className="analytics-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">{t('analytics.title')}</h1>
          <p className="page-subtitle">{t('analytics.subtitle')}</p>
        </div>
      </div>

      {/* Heatmap Section */}
      <Card className="heatmap-card">
        <CardHeader>
          <CardTitle className="chart-title">
            <MapPin size={20} />
            {t('analytics.heatmap')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Tabs value={selectedFloor.toString()} onValueChange={(v) => setSelectedFloor(parseInt(v))}>
            <TabsList className="heatmap-tabs">
              {FLOORS.map(floor => (
                <TabsTrigger key={floor.id} value={floor.id.toString()}>
                  {floor.name} - {floor.department}
                </TabsTrigger>
              ))}
            </TabsList>

            {FLOORS.map(floor => (
              <TabsContent key={floor.id} value={floor.id.toString()} className="heatmap-content">
                <div className="heatmap-legend">
                  <span>{t('analytics.lowActivity')}</span>
                  <div className="legend-gradient"></div>
                  <span>{t('analytics.highActivity')}</span>
                </div>
                
                <svg viewBox="0 0 630 310" className="heatmap-svg">
                  {heatmapData.map(room => (
                    <g key={room.id}>
                      <rect
                        x={room.x}
                        y={room.y}
                        width={room.width}
                        height={room.height}
                        fill={getHeatColor(room.heatLevel)}
                        stroke="#94A3B8"
                        strokeWidth="2"
                        className="heatmap-room"
                      />
                      <text
                        x={room.x + room.width / 2}
                        y={room.y + room.height / 2 - 5}
                        className="heatmap-room-id"
                      >
                        {room.id}
                      </text>
                      <text
                        x={room.x + room.width / 2}
                        y={room.y + room.height / 2 + 10}
                        className="heatmap-room-count"
                      >
                        {room.inUseCount} {t('analytics.active')}
                      </text>
                    </g>
                  ))}
                </svg>
              </TabsContent>
            ))}
          </Tabs>
        </CardContent>
      </Card>

      {/* Top Assets */}
      <div className="analytics-grid">
        <Card className="analytics-card">
          <CardHeader>
            <CardTitle className="chart-title">
              <TrendingUp size={20} />
              {t('analytics.mostUsed')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="asset-ranking-list">
              {mostUsedAssets.map((asset, idx) => (
                <div key={asset.id} className="ranking-item">
                  <div className="ranking-badge">{idx + 1}</div>
                  <div className="ranking-info">
                    <div className="ranking-name">{asset.name}</div>
                    <div className="ranking-detail">
                      {t('map.floor')} {asset.floor} - {asset.room} • {t(`status.${asset.status.replace(/-/g, '')}`)}
                    </div>
                  </div>
                  <div className="ranking-score">
                    <div className="score-bar" style={{ width: `${(asset.usageScore / 10) * 100}%` }}></div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card className="analytics-card">
          <CardHeader>
            <CardTitle className="chart-title">
              <AlertTriangle size={20} />
              {t('analytics.hardToTrack')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="asset-ranking-list">
              {lostAssets.map((asset, idx) => (
                <div key={asset.id} className="ranking-item">
                  <div className="ranking-badge warning">{idx + 1}</div>
                  <div className="ranking-info">
                    <div className="ranking-name">{asset.name}</div>
                    <div className="ranking-detail">
                      {t('assets.battery')}: {asset.batteryLevel}% • {t('analytics.moves')}: {asset.path?.length || 0}
                    </div>
                  </div>
                  <div className="ranking-score">
                    <div className="score-bar warning" style={{ width: `${Math.min((asset.lostScore / 15) * 100, 100)}%` }}></div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recommendations */}
      <Card className="recommendations-card">
        <CardHeader>
          <CardTitle className="chart-title">
            <BarChart3 size={20} />
            {t('analytics.recommendations')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="recommendations-list">
            {recommendations.length > 0 ? (
              recommendations.map((rec, idx) => (
                <div key={idx} className={`recommendation-item ${rec.type}`}>
                  {rec.type === 'alert' && <AlertTriangle size={20} />}
                  {rec.type === 'optimization' && <TrendingUp size={20} />}
                  {rec.type === 'info' && <BarChart3 size={20} />}
                  <div>
                    <div className="recommendation-title">{rec.title}</div>
                    <div className="recommendation-text">{rec.description}</div>
                  </div>
                </div>
              ))
            ) : (
              <div className="recommendation-item success">
                <BarChart3 size={20} />
                <div>
                  <div className="recommendation-title">{t('analytics.optimal')}</div>
                  <div className="recommendation-text">{t('analytics.wellBalanced')}</div>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Analytics;
